/**
 * Context for managing the currently selected venue.
 * Persists the selection to localStorage.
 */

import {
    createContext,
    useContext,
    useEffect,
    useState,
    type ReactNode,
  } from "react";
  import { useAuth } from "./AuthContext";
  
  interface VenueContextType {
    selectedVenueId: string | null;
    setSelectedVenueId: (venueId: string | null) => void;
  }
  
  const VenueContext = createContext<VenueContextType | undefined>(undefined);
  
  export function VenueProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
  
    // Initialize state with lazy initializer to read from localStorage once
    const [selectedVenueId, setSelectedVenueId] = useState<string | null>(() => {
      if (user?.id) {
        return localStorage.getItem(`selectedVenueId:${user.id}`);
      }
      return null;
    });
  
    // Persist selectedVenueId when it changes
    useEffect(() => {
      if (user?.id) {
        if (selectedVenueId) {
          localStorage.setItem(`selectedVenueId:${user.id}`, selectedVenueId);
        } else {
          localStorage.removeItem(`selectedVenueId:${user.id}`);
        }
      }
    }, [selectedVenueId, user?.id]);
  
    // Reset selection if user logs out (user becomes null)
    useEffect(() => {
      if (!user) {
        setSelectedVenueId(null);
      } else {
        const stored = localStorage.getItem(`selectedVenueId:${user.id}`);
        if(stored !== selectedVenueId) {
            setSelectedVenueId(stored);
        }
      }
    }, [user?.id]);
  
    return (
      <VenueContext.Provider value={{ selectedVenueId, setSelectedVenueId }}>
        {children}
      </VenueContext.Provider>
    );
  }
  
  export function useVenue() {
    const context = useContext(VenueContext);
    if (context === undefined) {
      throw new Error("useVenue must be used within a VenueProvider");
    }
    return context;
  }
